/**
 * Hero Search Section — GraphQL Storefront API search with exact matching + tag search
 * Animated placeholders + part request form on no results
 */
(function() {
  var section = document.querySelector('.hero-search');
  if (!section) return;

  var input = section.querySelector('.hero-search__input');
  var clearBtn = section.querySelector('.hero-search__clear');
  var resultsContainer = section.querySelector('.hero-search__results');
  var resultsInner = section.querySelector('.hero-search__results-inner');
  var viewAllLink = section.querySelector('.hero-search__results-view-all');

  var storeDomain = section.dataset.storeDomain || '';
  var storefrontToken = section.dataset.storefrontToken || '';
  var turnstileSiteKey = section.dataset.turnstileSitekey || '';
  var partRequestWidget = null;
  var debounceTimer = null;

  // GraphQL queries
  var SEARCH_QUERY = 'query searchProducts($query: String!, $first: Int!) { search(query: $query, first: $first, types: PRODUCT) { edges { node { ... on Product { id title handle tags productType onlineStoreUrl featuredImage { url altText } priceRange { minVariantPrice { amount currencyCode } } variants(first: 1) { edges { node { sku } } } } } } } }';

  var TAG_QUERY = 'query productsByTag($tag: String!, $first: Int!) { products(first: $first, query: $tag) { edges { node { id title handle tags productType onlineStoreUrl featuredImage { url altText } priceRange { minVariantPrice { amount currencyCode } } variants(first: 1) { edges { node { sku } } } } } } }';

  // --- Animated placeholder typing ---
  var placeholderTexts = (section.dataset.placeholders || '').split('|').filter(Boolean);
  if (placeholderTexts.length > 0) {
    var idx = 0, charIdx = 0, deleting = false, paused = false;

    function tick() {
      if (input === document.activeElement && input.value.length > 0) {
        setTimeout(tick, 500);
        return;
      }
      var current = placeholderTexts[idx];
      if (!deleting && !paused) {
        charIdx++;
        input.placeholder = current.slice(0, charIdx);
        if (charIdx === current.length) {
          paused = true;
          setTimeout(function() { paused = false; deleting = true; tick(); }, 2000);
          return;
        }
        setTimeout(tick, 60);
      } else if (deleting) {
        charIdx--;
        input.placeholder = current.slice(0, charIdx);
        if (charIdx === 0) {
          deleting = false;
          idx = (idx + 1) % placeholderTexts.length;
          setTimeout(tick, 400);
          return;
        }
        setTimeout(tick, 30);
      }
    }
    tick();
  }

  // --- Clear button ---
  input.addEventListener('input', function() {
    clearBtn.classList.toggle('is-visible', input.value.length > 0);
    if (input.value.length === 0) {
      resultsContainer.classList.remove('is-visible');
    } else {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() { doSearch(input.value.trim()); }, 300);
    }
  });

  clearBtn.addEventListener('click', function() {
    input.value = '';
    clearBtn.classList.remove('is-visible');
    resultsContainer.classList.remove('is-visible');
    input.focus();
  });

  // Close results on click outside
  document.addEventListener('click', function(e) {
    if (!section.contains(e.target)) {
      resultsContainer.classList.remove('is-visible');
    }
  });

  // Escape key
  input.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      resultsContainer.classList.remove('is-visible');
      input.blur();
    }
  });

  // --- Search via Storefront GraphQL API ---
  function doSearch(query) {
    if (query.length < 2) {
      resultsContainer.classList.remove('is-visible');
      return;
    }

    // If no Storefront token, fall back to predictive search API
    if (!storefrontToken) {
      doFallbackSearch(query);
      return;
    }

    resultsInner.innerHTML = '<div class="hero-search__results-loading">Searching...</div>';
    resultsContainer.classList.add('is-visible');
    viewAllLink.style.display = 'none';

    if (typeof gtag === 'function') gtag('event', 'search', { search_term: query });

    var apiUrl = 'https://' + storeDomain + '/api/2024-01/graphql.json';
    var headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontToken
    };

    // Run both search and tag queries in parallel
    Promise.all([
      fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ query: SEARCH_QUERY, variables: { query: query, first: 8 } })
      }).then(function(r) { return r.json(); }),
      fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ query: TAG_QUERY, variables: { tag: 'tag:' + query, first: 8 } })
      }).then(function(r) { return r.json(); })
    ])
    .then(function(results) {
      var searchData = results[0];
      var tagData = results[1];

      var searchProducts = (searchData.data && searchData.data.search && searchData.data.search.edges) || [];
      var tagProducts = (tagData.data && tagData.data.products && tagData.data.products.edges) || [];

      // Merge and deduplicate by product ID
      var seen = {};
      var rawProducts = [];
      var allEdges = searchProducts.concat(tagProducts);
      for (var i = 0; i < allEdges.length; i++) {
        var id = allEdges[i].node.id;
        if (!seen[id]) {
          seen[id] = true;
          rawProducts.push(allEdges[i]);
        }
      }

      // Filter: only keep products where the query actually appears in title, SKU, tags, or productType
      var queryLower = query.toLowerCase();
      var queryNormalized = queryLower.replace(/[-_\s]/g, '');
      var products = rawProducts.filter(function(edge) {
        var node = edge.node;
        var title = (node.title || '').toLowerCase();
        var sku = (node.variants && node.variants.edges && node.variants.edges[0] && node.variants.edges[0].node.sku || '').toLowerCase();
        var tags = (node.tags || []).join(' ').toLowerCase();
        var productType = (node.productType || '').toLowerCase();
        var titleNorm = title.replace(/[-_\s]/g, '');
        var skuNorm = sku.replace(/[-_\s]/g, '');
        var tagsNorm = tags.replace(/[-_\s]/g, '');
        var typeNorm = productType.replace(/[-_\s]/g, '');
        return (
          title.includes(queryLower) ||
          sku.includes(queryLower) ||
          tags.includes(queryLower) ||
          productType.includes(queryLower) ||
          titleNorm.includes(queryNormalized) ||
          skuNorm.includes(queryNormalized) ||
          tagsNorm.includes(queryNormalized) ||
          typeNorm.includes(queryNormalized)
        );
      });

      if (products.length === 0) {
        showNoResults(query);
        return;
      }

      renderResults(products, query);
    })
    .catch(function(err) {
      console.error('Search error:', err);
      resultsInner.innerHTML = '<div class="hero-search__results-empty">Something went wrong. Please try again.</div>';
    });
  }

  // --- Fallback: Shopify predictive search (if no Storefront token) ---
  function doFallbackSearch(query) {
    resultsInner.innerHTML = '<div class="hero-search__results-loading">Searching...</div>';
    resultsContainer.classList.add('is-visible');
    viewAllLink.style.display = 'none';

    var url = window.Shopify.routes.root + 'search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,vendor,tag,variants.sku';

    fetch(url)
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (products.length === 0) {
          showNoResults(query);
          return;
        }

        var html = '';
        products.forEach(function(product) {
          var img = product.image || '';
          var price = product.price;
          html += '<a href="' + product.url + '" class="hero-search__result">';
          if (img) html += '<img src="' + img + '" alt="" class="hero-search__result-img" loading="lazy">';
          html += '<div class="hero-search__result-info">';
          html += '<div class="hero-search__result-title">' + escapeHtml(product.title) + '</div>';
          html += '<div class="hero-search__result-meta">';
          if (product.vendor) html += '<span>' + escapeHtml(product.vendor) + '</span>';
          html += '</div></div>';
          html += '<div class="hero-search__result-price">$' + parseFloat(price).toFixed(2) + '</div>';
          html += '</a>';
        });

        html += buildNotFoundToggle(query);
        resultsInner.innerHTML = html;
        bindNotFoundToggle();
        bindPartRequestForm();

        viewAllLink.href = window.Shopify.routes.root + 'search?q=' + encodeURIComponent(query) + '&type=product';
        viewAllLink.style.display = 'block';
      })
      .catch(function() {
        resultsInner.innerHTML = '<div class="hero-search__results-empty">Something went wrong. Please try again.</div>';
      });
  }

  // --- Render GraphQL search results ---
  function renderResults(products, query) {
    var storeUrl = 'https://' + storeDomain;
    var html = '';
    products.forEach(function(edge) {
      var node = edge.node;
      var price = parseFloat(node.priceRange.minVariantPrice.amount);
      var sku = (node.variants && node.variants.edges && node.variants.edges[0] && node.variants.edges[0].node.sku) || '';
      var imgUrl = (node.featuredImage && node.featuredImage.url) || '';
      var url = node.onlineStoreUrl || (storeUrl + '/products/' + node.handle);

      html += '<a href="' + url + '" class="hero-search__result">';
      if (imgUrl) {
        html += '<img src="' + imgUrl + '&width=100" alt="" class="hero-search__result-img" loading="lazy">';
      }
      html += '<div class="hero-search__result-info">';
      html += '<div class="hero-search__result-title">' + escapeHtml(node.title) + '</div>';
      html += '<div class="hero-search__result-meta">';
      if (sku) html += '<span>SKU: ' + escapeHtml(sku) + '</span>';
      html += '</div></div>';
      html += '<div class="hero-search__result-price">$' + price.toFixed(2) + '</div>';
      html += '</a>';
    });

    html += buildNotFoundToggle(query);
    resultsInner.innerHTML = html;
    bindNotFoundToggle();
    bindPartRequestForm();

    viewAllLink.href = 'https://' + storeDomain + '/search?q=' + encodeURIComponent(query) + '&type=product';
    viewAllLink.style.display = 'block';
  }

  // --- "Not what you're looking for?" toggle ---
  function buildNotFoundToggle(query) {
    return '<div class="hero-search__not-found">' +
      '<button type="button" class="hero-search__not-found-toggle" id="hero-not-found-toggle">' +
      'Not what you\'re looking for? <span style="color:var(--hs-color-accent)">Request a part &darr;</span></button>' +
      '<div class="hero-search__not-found-form" id="hero-not-found-form" style="display:none;">' +
        buildPartRequestFormHtml(query) +
      '</div></div>';
  }

  function bindNotFoundToggle() {
    var toggleBtn = document.getElementById('hero-not-found-toggle');
    var toggleForm = document.getElementById('hero-not-found-form');
    if (toggleBtn && toggleForm) {
      toggleBtn.addEventListener('click', function() {
        toggleForm.style.display = toggleForm.style.display === 'none' ? 'block' : 'none';
      });
    }
  }

  // --- Part request form HTML ---
  function buildPartRequestFormHtml(query) {
    return '<form class="part-request-form" id="hero-part-request-form">' +
      '<div class="part-request-form__row">' +
        '<input type="text" class="part-request-form__input" name="part_number" placeholder="Part #" value="' + escapeHtml(query) + '">' +
      '</div>' +
      '<div class="part-request-form__row">' +
        '<input type="text" class="part-request-form__input" name="first_name" placeholder="First Name" required>' +
        '<input type="text" class="part-request-form__input" name="last_name" placeholder="Last Name" required>' +
      '</div>' +
      '<div class="part-request-form__row">' +
        '<input type="email" class="part-request-form__input" name="email" placeholder="Email" required>' +
        '<input type="tel" class="part-request-form__input" name="phone" placeholder="Phone">' +
      '</div>' +
      '<div class="part-request-form__row part-request-form__turnstile" id="hero-part-request-turnstile"></div>' +
      '<div class="part-request-form__row">' +
        '<button type="submit" class="part-request-form__btn part-request-form__btn--full">Submit Part Request</button>' +
      '</div>' +
      '<div class="part-request-form__status" id="hero-part-request-status"></div>' +
    '</form>';
  }

  function bindPartRequestForm() {
    var form = document.getElementById('hero-part-request-form');
    partRequestWidget = null;
    if (form && window.mbsTurnstile) {
      window.mbsTurnstile
        .render(document.getElementById('hero-part-request-turnstile'), turnstileSiteKey)
        .then(function (id) { partRequestWidget = id; });
    }
    if (form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        submitPartRequest(form);
      });
    }
  }

  // --- No results view ---
  function showNoResults(query) {
    viewAllLink.style.display = 'none';
    resultsInner.innerHTML =
      '<div class="hero-search__results-empty">' +
        '<div class="hero-search__empty-title">Can\'t find your part?</div>' +
        '<div class="hero-search__empty-text">We can get it for you, usually within 24 hours.</div>' +
        buildPartRequestFormHtml(query) +
        '<div class="hero-search__empty-phone">Or call us: <a href="tel:8885659483" style="color:inherit;text-decoration:underline;">(888) 565-9483</a></div>' +
      '</div>';
    bindPartRequestForm();
  }

  // --- Submit part request ---
  function submitPartRequest(form) {
    var btn = form.querySelector('.part-request-form__btn');
    var statusEl = document.getElementById('hero-part-request-status');
    var formData = new FormData(form);
    var data = {
      firstName: formData.get('first_name') || '',
      lastName: formData.get('last_name') || '',
      email: formData.get('email') || '',
      phone: formData.get('phone') || undefined,
      partNumber: formData.get('part_number') || ''
    };

    if (!data.firstName || !data.email) {
      statusEl.textContent = 'Please fill in your name and email.';
      statusEl.className = 'part-request-form__status part-request-form__status--error';
      return;
    }

    data.turnstileToken = window.mbsTurnstile ? window.mbsTurnstile.token(partRequestWidget) : '';
    if (!data.turnstileToken) {
      statusEl.textContent = 'Please complete the verification check above the button.';
      statusEl.className = 'part-request-form__status part-request-form__status--error';
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Submitting...';

    fetch('https://www.megaboxcentral.com/api/public/part-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(function(res) {
      return res.json().catch(function() { return {}; }).then(function(body) {
        if (!res.ok) {
          var err = new Error('Server error');
          if (res.status === 403 || res.status === 429) err.userMessage = body.error;
          throw err;
        }
        return body;
      });
    })
    .then(function() {
      statusEl.textContent = 'Request submitted! We\'ll get back to you soon.';
      statusEl.className = 'part-request-form__status part-request-form__status--success';
      btn.textContent = 'Submitted';
      if (typeof gtag === 'function') {
        gtag('event', 'part_request', { part_number: data.partNumber, email: data.email });
      }
    })
    .catch(function(err) {
      statusEl.textContent = (err && err.userMessage) || 'Something went wrong. Please call (888) 565-9483.';
      statusEl.className = 'part-request-form__status part-request-form__status--error';
      btn.disabled = false;
      btn.textContent = 'Submit Part Request';
      if (window.mbsTurnstile) window.mbsTurnstile.reset(partRequestWidget);
    });
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
})();
